/**
 * Onboarding Tour Component - Enhanced Version
 * Mobil ve masaüstü için optimize edilmiş interaktif öğrenme turu
 * 
 * Özellikler:
 * - Spotlight efekti ile element vurgulama
 * - Tooltip ve modal desteği
 * - İlerleme çubuğu
 * - Mobil/Masaüstü için optimize edilmiş responsive tasarım
 * - Klavye navigasyonu
 * - Checklist sistemi
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../context/OnboardingContext';
import './OnboardingTour.css';

// Badge renkleri
const BADGE_COLORS = {
    starter: { bg: '#3b82f6', text: '#fff' },
    pro: { bg: '#8b5cf6', text: '#fff' },
    enterprise: { bg: '#f59e0b', text: '#000' }
};

// Spotlight bileşeni - element vurgulama
const Spotlight = ({ targetRect, padding = 12 }) => {
    if (!targetRect) return null;

    const { top, left, width, height } = targetRect;

    return (
        <div className="onboarding-spotlight">
            <div
                className="onboarding-spotlight__hole"
                style={{
                    top: top - padding,
                    left: left - padding,
                    width: width + (padding * 2),
                    height: height + (padding * 2)
                }}
            />
            {/* Ek parlak çerçeve overlay */}
            <div
                className="onboarding-spotlight__glow-border"
                style={{
                    position: 'absolute',
                    top: top - padding - 2,
                    left: left - padding - 2,
                    width: width + (padding * 2) + 4,
                    height: height + (padding * 2) + 4,
                    borderRadius: '14px',
                    border: '2px solid rgba(59, 130, 246, 0.8)',
                    pointerEvents: 'none',
                    animation: 'borderPulse 1.5s ease-in-out infinite'
                }}
            />
        </div>
    );
};

// Tooltip bileşeni - Mobil ve masaüstü için optimize edilmiş
const Tooltip = ({ step, targetRect, onNext, onPrev, onSkip, currentStep, totalSteps, progress, isMobile }) => {
    const tooltipRef = useRef(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const navigate = useNavigate();

    // Tooltip pozisyonunu hesapla
    useEffect(() => {
        if (!tooltipRef.current) return;

        const tooltip = tooltipRef.current;
        const tooltipRect = tooltip.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const padding = isMobile ? 12 : 16;

        let top = 0;
        let left = 0;

        if (step.type === 'modal' || !targetRect) {
            // Modal - ekranın ortasında
            top = (viewportHeight - tooltipRect.height) / 2;
            left = (viewportWidth - tooltipRect.width) / 2;
        } else {
            // Spotlight - target'a göre pozisyonla
            const { top: tTop, left: tLeft, width: tWidth, height: tHeight } = targetRect;

            // Mobilde her zaman alt veya üst pozisyon kullan
            const effectivePosition = isMobile ?
                (step.position === 'left' || step.position === 'right' ? 'bottom' : step.position) :
                step.position;

            switch (effectivePosition) {
                case 'top':
                    top = tTop - tooltipRect.height - padding;
                    left = tLeft + (tWidth - tooltipRect.width) / 2;
                    break;
                case 'bottom':
                    top = tTop + tHeight + padding;
                    left = tLeft + (tWidth - tooltipRect.width) / 2;
                    break;
                case 'left':
                    top = tTop + (tHeight - tooltipRect.height) / 2;
                    left = tLeft - tooltipRect.width - padding;
                    break;
                case 'right':
                    top = tTop + (tHeight - tooltipRect.height) / 2;
                    left = tLeft + tWidth + padding;
                    break;
                case 'bottom-left':
                    top = tTop + tHeight + padding;
                    left = tLeft + tWidth - tooltipRect.width;
                    break;
                default:
                    top = tTop + tHeight + padding;
                    left = tLeft + (tWidth - tooltipRect.width) / 2;
            }

            // Viewport sınırları içinde tut
            if (left < padding) left = padding;
            if (left + tooltipRect.width > viewportWidth - padding) {
                left = viewportWidth - tooltipRect.width - padding;
            }
            if (top < padding) top = padding;
            if (top + tooltipRect.height > viewportHeight - padding) {
                top = viewportHeight - tooltipRect.height - padding;
            }
        }

        setPosition({ top, left });
    }, [step, targetRect, isMobile]);

    const handleAction = () => {
        if (step.action?.path) {
            navigate(step.action.path);
        }
        onNext();
    };

    const badge = step.badge ? BADGE_COLORS[step.badge] : null;

    return (
        <div
            ref={tooltipRef}
            className={`onboarding-tooltip onboarding-tooltip--${step.type} onboarding-tooltip--${step.position || 'center'} ${isMobile ? 'onboarding-tooltip--mobile' : ''}`}
            style={{ top: position.top, left: position.left }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="onboarding-title"
        >
            {/* İlerleme çubuğu */}
            <div className="onboarding-tooltip__progress">
                <div
                    className="onboarding-tooltip__progress-bar"
                    style={{ width: `${progress}%` }}
                />
            </div>

            {/* Badge */}
            {badge && (
                <div
                    className="onboarding-tooltip__badge"
                    style={{ backgroundColor: badge.bg, color: badge.text }}
                >
                    {step.badge.toUpperCase()}
                </div>
            )}

            {/* Başlık */}
            <h3 id="onboarding-title" className="onboarding-tooltip__title">
                {step.title}
            </h3>

            {/* İçerik */}
            <p className="onboarding-tooltip__content">
                {step.content}
            </p>

            {/* Aksiyonlar */}
            <div className="onboarding-tooltip__actions">
                <div className="onboarding-tooltip__nav">
                    {currentStep > 0 && (
                        <button
                            className="onboarding-btn onboarding-btn--secondary"
                            onClick={onPrev}
                            aria-label="Önceki adım"
                        >
                            <i className="fas fa-arrow-left" /> {!isMobile && 'Geri'}
                        </button>
                    )}
                </div>

                <div className="onboarding-tooltip__step-info">
                    {currentStep + 1} / {totalSteps}
                </div>

                <div className="onboarding-tooltip__nav">
                    {step.action ? (
                        <button
                            className="onboarding-btn onboarding-btn--primary"
                            onClick={handleAction}
                        >
                            {step.action.label} <i className="fas fa-arrow-right" />
                        </button>
                    ) : step.isLast ? (
                        <button
                            className="onboarding-btn onboarding-btn--success"
                            onClick={onNext}
                        >
                            {isMobile ? '' : 'Tamamla'} <i className="fas fa-check" />
                        </button>
                    ) : (
                        <button
                            className="onboarding-btn onboarding-btn--primary"
                            onClick={onNext}
                            aria-label="Sonraki adım"
                        >
                            {isMobile ? '' : 'İleri'} <i className="fas fa-arrow-right" />
                        </button>
                    )}
                </div>
            </div>

            {/* Atla butonu */}
            <button
                className="onboarding-tooltip__skip"
                onClick={onSkip}
                aria-label="Turu atla"
            >
                <i className="fas fa-times" />
            </button>

            {/* Klavye ipucu - sadece masaüstünde */}
            {!isMobile && (
                <div className="onboarding-tooltip__keyboard-hint">
                    <span><kbd>←</kbd> <kbd>→</kbd> Gezin</span>
                    <span><kbd>ESC</kbd> Çık</span>
                </div>
            )}
        </div>
    );
};

// Minimized indicator
const MinimizedIndicator = ({ onClick, progress }) => (
    <button
        className="onboarding-minimized"
        onClick={onClick}
        aria-label="Turu devam ettir"
    >
        <div className="onboarding-minimized__icon">
            <i className="fas fa-graduation-cap" />
        </div>
        <div className="onboarding-minimized__progress">
            <div
                className="onboarding-minimized__progress-bar"
                style={{ width: `${progress}%` }}
            />
        </div>
        <span className="onboarding-minimized__text">Turu Devam Ettir</span>
    </button>
);

// Ana Tour bileşeni
export default function OnboardingTour() {
    const {
        isActive,
        isMinimized,
        isMobile,
        currentStepData,
        currentStep,
        steps,
        progress,
        nextStep,
        prevStep,
        endTour,
        toggleMinimize
    } = useOnboarding();

    const [targetRect, setTargetRect] = useState(null);

    // Target elementi bul ve pozisyonunu al
    const findTarget = useCallback(() => {
        if (!currentStepData?.target) {
            setTargetRect(null);
            return;
        }

        const target = document.querySelector(currentStepData.target);
        if (target) {
            const rect = target.getBoundingClientRect();
            setTargetRect({
                top: rect.top + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
                height: rect.height
            });

            // Elementi görünür yap
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            setTargetRect(null);
        }
    }, [currentStepData]);

    // Target değiştiğinde pozisyonu güncelle
    useEffect(() => {
        if (!isActive || isMinimized) return;

        findTarget();

        // Resize event listener
        const handleResize = () => findTarget();
        window.addEventListener('resize', handleResize);

        // Scroll event listener  
        const handleScroll = () => findTarget();
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isActive, isMinimized, currentStepData, findTarget]);

    if (!isActive) return null;

    if (isMinimized) {
        return <MinimizedIndicator onClick={toggleMinimize} progress={progress} />;
    }

    return (
        <div className="onboarding-overlay" role="presentation">
            {/* Spotlight overlay */}
            {currentStepData?.type === 'spotlight' && targetRect && (
                <Spotlight targetRect={targetRect} />
            )}

            {/* Modal overlay */}
            {currentStepData?.type === 'modal' && (
                <div className="onboarding-modal-overlay" />
            )}

            {/* Tooltip */}
            {currentStepData && (
                <Tooltip
                    step={currentStepData}
                    targetRect={targetRect}
                    onNext={nextStep}
                    onPrev={prevStep}
                    onSkip={() => endTour(false)}
                    currentStep={currentStep}
                    totalSteps={steps.length}
                    progress={progress}
                    isMobile={isMobile}
                />
            )}
        </div>
    );
}

// Yardım butonu - mobil ve masaüstü için optimize edilmiş
export function OnboardingHelpButton() {
    const { startTour, isActive, isMobile } = useOnboarding();
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef(null);
    const buttonRef = useRef(null);
    const navigate = useNavigate();

    // Landing page ve public sayfalarda gösterme
    const currentPath = window.location.pathname;
    const publicPaths = ['/', '/register', '/login', '/forgot-password', '/reset-password', '/verify-email', '/privacy-policy', '/terms-of-service', '/kvkk', '/refund-policy', '/distance-sales-agreement', '/coming-soon', '/tools', '/developers'];
    const isPublicPage = publicPaths.some(path => currentPath === path || currentPath.startsWith('/tools/'));

    // Dışarı tıklama kontrolü
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target) &&
                buttonRef.current && !buttonRef.current.contains(event.target)) {
                setShowMenu(false);
            }
        };

        if (showMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showMenu]);

    if (isActive || isPublicPage) return null;

    // Session'dan plan bilgisini al
    const handleStartTour = (tourType) => {
        const sessionStr = localStorage.getItem('session');
        let plan = 'starter';
        try {
            const session = JSON.parse(sessionStr || '{}');
            plan = session.plan || 'starter';
        } catch (e) { }

        startTour(tourType, plan);
        setShowMenu(false);
    };

    // API kurulumu turu - mobilde masaüstüne yönlendir
    const handleApiSetup = () => {
        if (isMobile) {
            // Mobilde direkt ayarlara git
            navigate('/settings');
            setShowMenu(false);
        } else {
            handleStartTour('apiSetup');
        }
    };

    return (
        <div className={`onboarding-help-container ${isMobile ? 'onboarding-help-container--mobile' : ''}`}>
            {showMenu && (
                <div ref={menuRef} className="onboarding-help-menu">
                    <button onClick={() => handleStartTour('main')}>
                        <i className="fas fa-play-circle" />
                        {isMobile ? 'Tur Başlat' : 'Genel Tur'}
                    </button>
                    <button onClick={handleApiSetup}>
                        <i className="fas fa-plug" />
                        {isMobile ? 'Ayarlar' : 'API Kurulumu'}
                    </button>
                    {!isMobile && (
                        <button onClick={() => { navigate('/settings'); setShowMenu(false); }}>
                            <i className="fas fa-cog" /> Mağaza Ayarları
                        </button>
                    )}
                </div>
            )}
            <button
                ref={buttonRef}
                className="onboarding-help-button"
                onClick={() => setShowMenu(!showMenu)}
                aria-label="Yardım ve Öğrenme Turları"
                title="Yardım"
            >
                <i className="fas fa-question" />
            </button>
        </div>
    );
}
